import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { getEventDateTime, getEventEndDateTime, isEventActive } from '@/lib/helpers';
import { CATEGORY_LABELS } from '@/constants/categories';
import { formatTime } from '../utils/eventUtils';

// ─── helpers locais ────────────────────────────────────────────────────────

function toDate(raw) {
  if (!raw) return null;
  return raw.toDate ? raw.toDate() : new Date(raw);
}

function isToday(date) {
  if (!date) return false;
  const now = new Date();
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  );
}

// Converte um documento Firestore para o shape que a UI pública espera.
function normalizeEvent(docId, data) {
  const startDate = getEventDateTime(data);   // reutiliza helper existente
  const endDate   = getEventEndDateTime(data); // reutiliza helper existente

  const categoryLabel = CATEGORY_LABELS[data.category] || data.category || '';

  // Formata dateLabel legível (ex: "Sáb, 12 Jul · 19:00")
  let dateLabel = '';
  if (startDate) {
    const now = new Date();
    const todayStr = startDate.toDateString() === now.toDateString();
    const dayFormatter = new Intl.DateTimeFormat('pt-BR', { weekday: 'short', day: 'numeric', month: 'short' });
    const timeFormatter = new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' });
    const dayPart  = todayStr ? 'Hoje' : dayFormatter.format(startDate);
    const timePart = timeFormatter.format(startDate);
    dateLabel = `${dayPart} · ${timePart}`;
  }

  return {
    id:            docId,
    title:         data.title         || '',
    description:   data.description   || '', // usado pela busca da Home (ver eventUtils.matchesSearch)
    category:      data.category      || '',
    categoryLabel,
    dateLabel,
    location:      data.location      || '',
    city:          data.city          || '',
    isFree:        Boolean(data.isFree),
    ticketPrice:   data.ticketPrice   ?? 0,
    imageUrl:      data.imageUrl      || '',
    boosted:       Boolean(data.boosted),
    boostValue:    data.boostValue    ?? 0,
    _startDate:    startDate,  // usado internamente para ordenação e filtros
    _endDate:      endDate,
    _raw:          data,       // passado para isEventActive
  };
}

// ─── ordenação (mesma lógica de eventService.js) ──────────────────────────

function sortByBoostThenDate(a, b) {
  if (a.boosted && !b.boosted) return -1;
  if (!a.boosted && b.boosted) return 1;
  if (a.boosted && b.boosted) {
    if (a.boostValue !== b.boostValue) return b.boostValue - a.boostValue;
  }
  const tA = a._startDate?.getTime() ?? 0;
  const tB = b._startDate?.getTime() ?? 0;
  return tA - tB; // mais próximo primeiro
}

// ─── normalização completa (para página de detalhe) ──────────────────────

function normalizeEventDetail(docId, data) {
  const startDate = getEventDateTime(data);
  const endDate   = getEventEndDateTime(data);

  const categoryLabel = CATEGORY_LABELS[data.category] || data.category || '';

  return {
    id:           docId,
    title:        data.title       || '',
    description:  data.description || '',
    category:     data.category    || '',
    categoryLabel,
    location:     data.location    || '',
    city:         data.city        || '',
    latitude:     data.latitude    ?? null,
    longitude:    data.longitude   ?? null,
    isFree:       Boolean(data.isFree),
    ticketPrice:  data.ticketPrice ?? 0,
    ticketUrl:    data.ticketUrl   || '',
    imageUrl:     data.imageUrl    || '',
    organizerId:  data.organizerId || data.createdBy || '',
    status:       data.status      || '',
    boosted:      Boolean(data.boosted),
    views:        data.views       ?? 0,
    startTime:    formatTime(startDate),
    endTime:      formatTime(endDate),
    _startDate:   startDate,
    _endDate:     endDate,
    _raw:         data,
  };
}

// ─── serviço público ──────────────────────────────────────────────────────

export const publicEventService = {
  // Busca todos os eventos aprovados e retorna os 3 grupos processados.
  // Uma única query evita múltiplas leituras; o filtro por grupo é feito
  // em memória (volume pequeno, cidade única).
  async getHomeEvents() {
    const q = query(
      collection(db, 'events'),
      where('status', '==', 'approved')
    );

    const snapshot = await getDocs(q);
    const all = snapshot.docs.map(d => normalizeEvent(d.id, d.data()));

    const now = new Date();

    // 1. Acontecendo Hoje: startDate é hoje E evento ainda não encerrou
    const today = all
      .filter(e => isToday(e._startDate) && isEventActive(e._raw))
      .sort((a, b) => (a._startDate?.getTime() ?? 0) - (b._startDate?.getTime() ?? 0));

    // 2. Eventos em Destaque: boosted=true e ainda ativos
    const featured = all
      .filter(e => e.boosted && isEventActive(e._raw))
      .sort(sortByBoostThenDate);

    // 3. Próximos Eventos: startDate > agora (ainda não começou), excluindo
    // os que já entram em "Hoje" — OU eventos de vários dias que começaram
    // antes de hoje mas ainda não encerraram (isEventActive), pra um
    // festival em andamento continuar aparecendo aqui em vez de sumir da
    // lista assim que a data de início fica no passado. Mesma janela
    // "start <= agora <= fim" que isEventLive() usa pro badge "Agora" —
    // sem isso, um evento ao vivo podia nunca chegar a esta lista pra o
    // badge ter a chance de aparecer.
    const upcoming = all
      .filter(e => {
        if (!e._startDate) return false;
        if (isToday(e._startDate)) return false;
        if (e._startDate.getTime() > now.getTime()) return true;
        return isEventActive(e._raw);
      })
      .sort((a, b) => (a._startDate?.getTime() ?? 0) - (b._startDate?.getTime() ?? 0));

    return { today, featured, upcoming };
  },

  // Busca eventos aprovados de uma categoria (página /categoria/:slug).
  // Duas cláusulas de igualdade (status + category) não exigem índice
  // composto no Firestore.
  async getEventsByCategory(categorySlug) {
    const q = query(
      collection(db, 'events'),
      where('status', '==', 'approved'),
      where('category', '==', categorySlug)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs
      .map(d => normalizeEvent(d.id, d.data()))
      .sort(sortByBoostThenDate);
  },

  // Busca um único evento por ID.
  // Retorna null se não existir ou não estiver aprovado.
  async getEventById(id) {
    const docSnap = await getDoc(doc(db, 'events', id));
    if (!docSnap.exists()) return null;

    const data = docSnap.data();
    if (data.status !== 'approved') return null;

    return normalizeEventDetail(docSnap.id, data);
  },
};
